import { defineInstance } from '@omni/instance'
import { natevaClaude } from './claude.nateva'

// Nateva (NZ forestry and land assets, Parnell, Auckland). Fractional AI
// Officer retainer from 12 Oct 2026; Duncan Southwell runs the programme,
// Renee Leightley (CFO) sponsors it. A SHELL-ONLY tenant on Nateva's own
// Supabase project (factory manifest data/customer-command-centers/nateva.json
// in EDNA-Command-Center): the Programme tab reads public.programme_* from
// nateva-platform supabase/migrations/0002_programme.sql.
//
// Brand, checked 10 Oct 2026 on nateva.co.nz (uicore-global.css): navy
// #003054, cyan #30C3E8, electric yellow #FCFC44, stone #EEECE3. The site's
// face is Sole Sans (an Adobe Fonts kit licensed to Nateva); the shell uses
// Inter, the site's own declared fallback. Logos are the site's own files:
// the navy wordmark (Nateva_logo_pos@4x.png) and the white reverse.
export const nateva = defineInstance({
  slug: 'nateva',
  displayName: 'Nateva',
  theme: {
    accent: '#003054',
    accentMuted: '#DFF5FB',
    gradient: 'linear-gradient(135deg, #002540 0%, #003054 60%, #0A3F68 100%)',
    logoUrl: '/branding/nateva-mark.png',
    wordmarkUrl: '/branding/nateva-logo.png',
    faviconUrl: '/branding/nateva-favicon.png',
    fontFamily: 'Inter',
    fontUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
    // Nateva is a light brand: navy on warm stone.
    forceLightScheme: true,
  },
  // Sign-in entry: the white wordmark on Nateva navy, yellow as the highlight
  // (the same treatment as the site's reverse wordmark on navy).
  entry: {
    tagline: 'Data platform · Apps · Programme',
    background: 'radial-gradient(120% 85% at 50% 0%, #0A4C7A 0%, #003054 55%, #001A2E 100%)',
    logoUrl: '/branding/nateva-logo-white.png',
    logoHeight: 64,
    highlight: '#FCFC44',
    glow: 'rgba(48, 195, 232, 0.32)',
    // Code only, no link in the email (25 Oct 2026): Nateva's mail filter held
    // the link email for minutes while it scanned the link. Matches auth.email
    // = "code" in Command-Center data/omni-tenants.json.
    codeOnly: true,
    emailHint: 'Use your Nateva email address. We email you a 6-digit code.',
    emailPlaceholder: 'you@nateva.co.nz',
    // The cover (Sam, 16 Oct 2026: open like the NZ Funds Client Intelligence
    // cover). Stone wordmark, "Intelligence" in yellow as the kit's lockup,
    // the field team photo from the kit (nateva.co.nz) behind the card.
    cover: {
      eyebrow: 'One sign-in for every Nateva app',
      title: 'Intelligence',
      line: "All of Nateva's data in one place, with focused apps on top, so every team can see and work with what they need.",
      photoUrl: '/branding/nateva-field-team.webp',
      footer: 'Nateva Intelligence',
    },
  },
  // The front-door site's tabs (doc customer-front-door, section 8): Apps
  // (the front page, first and the landing tab), Programme (the shell-owned
  // `home` surface, relabelled), Team (what is going on across the five apps,
  // Sam 19 Oct 2026) and Using Claude. Each Nateva app is its own repo and
  // site (nateva-field, nateva-partners, nateva-agreements, nateva-yield);
  // the front page's tiles open them.
  // People and access (Sam, 19 Oct 2026; Command-Center docs/people-tab.md).
  // Brand (Sam, 25 Oct 2026): the kit's brand and document templates, from the person menu.
  // Reports (Sam, 25 Oct 2026; doc nateva-intelligence-reports 4.9): finished reports by reader.
  // Health (Sam, 3 Nov 2026): is everything working and safe; admins only.
  modules: ['front-door', 'home', 'reports', 'team', 'brand', 'health', 'people', 'learn', 'notifications'],
  navOrder: ['front-door', 'home', 'reports', 'team', 'brand', 'health', 'people', 'learn', 'notifications'],
  // The Notifications page is opened from the bell and the person menu, never as a tab (Sam, 3 Nov 2026).
  navHidden: ['notifications'],
  copy: { productName: 'Nateva Intelligence', surfaceLabels: { home: 'Programme', people: 'People and access' } },
  // Customer front door (doc customer-front-door, sections 4 and 5). One
  // sign-in on the front-door site (natevaintelligence.co.nz).
  // Microsoft sign-in is offered above the email link once Azure is switched
  // on in Nateva's Supabase project (needs the Entra registration from
  // Nateva's IT). The shared cookie is only used on pages served under
  // cookieDomain; localhost and *.netlify.app drafts keep per-host sign-in.
  // CC scripts/tenant/front-door-add-app.mjs reads `apps` below as text: one
  // `{ slug, subdomain, modules }` per line.
  frontDoor: {
    // natevaintelligence.co.nz (bought 25 Oct 2026) is the front door; the
    // apps open on <app>.natevaintelligence.co.nz (access.app url). No
    // cookieDomain: each app keeps its own sign-in in localStorage and never
    // reads the shared cookie.
    domain: 'natevaintelligence.co.nz',
    // Email link or code only (Sam, 16 Oct 2026: no Microsoft sign-in). Any
    // @nateva.co.nz address, and Sam, can get an account; Nateva's database
    // refuses every other address (CC configure-auth.mjs, sign_in_emails).
    providers: ['email'],
    // Nateva's own launcher, not our Command Centre (Sam, 16 Oct 2026): a
    // slim Nateva Intelligence top bar on every page, the front page a grid
    // of the person's apps, Programme reached from its strip and the menu.
    layout: 'launcher',
    home: { modules: ['front-door', 'home', 'reports', 'team', 'brand', 'health', 'people', 'learn', 'notifications'] },
    // No app is a build of this shell any more: the CRM placeholder retired
    // when Nateva Partners took its place (19 Oct 2026).
    apps: [],
  },
  claude: natevaClaude,
})
